/**
 * A table node view that keeps the node's own attributes on the `<table>`.
 *
 * TipTap's `TableView` builds its DOM by hand — `div.tableWrapper > table >
 * colgroup + tbody` — and never applies the node's `renderHTML` attributes to
 * the element it creates. That is fine for a stock table, whose only attributes
 * are the ones `colgroup` already encodes, and silently wrong for ours:
 * `headerBgColor` renders as `data-header-bg-color` on the `<table>`, and the
 * CSS that paints the header row keys off exactly that attribute.
 *
 * The node view is only installed when column resizing is on, so turning
 * resizing on is what broke the header-colour picker: the attribute was still
 * written to the document and still emitted by `renderHTML` (so the clipboard
 * and any non-node-view render kept it), but the live editor stopped showing
 * it. Picking a colour appeared to do nothing at all.
 *
 * Re-applying on `update` is required, not belt-and-braces — picking a colour
 * dispatches an attribute change on the same node, which ProseMirror routes to
 * `update()` rather than rebuilding the view.
 */

import { TableView } from '@tiptap/extension-table'
import type { Node as PMNode } from '@tiptap/pm/model'

const HEADER_BG_ATTR = 'data-header-bg-color'

function syncTableAttributes(table: HTMLElement, node: PMNode): void {
  const color = node.attrs.headerBgColor
  if (typeof color === 'string' && color.length > 0) {
    table.setAttribute(HEADER_BG_ATTR, color)
  } else {
    table.removeAttribute(HEADER_BG_ATTR)
  }
}

export class WikiTableView extends TableView {
  constructor(node: PMNode, cellMinWidth: number) {
    super(node, cellMinWidth)
    syncTableAttributes(this.table, node)
  }

  override update(node: PMNode): boolean {
    const handled = super.update(node)
    if (handled) syncTableAttributes(this.table, node)
    return handled
  }
}
